import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext();

const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined 
  ? process.env.REACT_APP_API_URL 
  : (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

export const AuthProvider = ({ children }) => {
  // Load initial user state from localStorage (null by default if not signed in)
  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem('skillmesh_user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        if (parsed && parsed.email) return parsed;
      } catch (e) {
        console.error('Error parsing stored user', e);
      }
    }
    return null;
  });

  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Sync currentUser with localStorage
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('skillmesh_user', JSON.stringify(currentUser));
    } else {
      localStorage.removeItem('skillmesh_user');
    }
  }, [currentUser]);

  // Request Magic Link
  const sendMagicLink = async (email, redirectUrl = window.location.origin + '/login') => {
    setLoading(true);
    setAuthError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, redirect_url: redirectUrl })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send magic link. Please try again.');
      }

      setLoading(false);
      return data;
    } catch (err) {
      setLoading(false);
      setAuthError(err.message);
      throw err;
    }
  };

  // Verify Magic Token and perform login
  const verifyMagicToken = async (token) => {
    setLoading(true);
    setAuthError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Invalid or expired magic link. Please request a new one.');
      }

      const resData = await response.json();
      const savedPeople = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      const userEmail = (resData.email || '').toLowerCase().trim();
      const existingPerson = savedPeople.find(p => (p.email || '').toLowerCase().trim() === userEmail);

      const userSkills = existingPerson?.detailedSkills || 
        (existingPerson?.skills || []).map(s => typeof s === 'string' ? { name: s, level: 'Intermediate', category: 'General', experienceYears: 1 } : s);

      const updatedUser = {
        email: resData.email,
        name: existingPerson?.name || resData.name,
        role: resData.role,
        title: existingPerson?.role || (resData.role === 'admin' ? 'SkillMesh Administrator' : 'SkillMesh Member'),
        bio: existingPerson?.bio || '',
        avatar: existingPerson?.avatar || 'dev-1',
        location: existingPerson?.location || 'Bucharest, Romania',
        availability: existingPerson?.availability || 'Available',
        skills: userSkills,
        certifications: existingPerson?.certifications || [],
        projects: existingPerson?.projects || []
      };

      if (!existingPerson) {
        const newPerson = {
          id: `p_${Date.now()}`,
          name: updatedUser.name,
          role: updatedUser.title,
          team: 'Engineering',
          email: updatedUser.email,
          location: updatedUser.location,
          availability: 'Available',
          skills: [],
          rating: 5.0,
          experience: '1 years',
          bio: '',
          avatar: updatedUser.avatar,
          certifications: []
        };
        savedPeople.push(newPerson);
        localStorage.setItem('skillmesh_people', JSON.stringify(savedPeople));
        api.createPerson(newPerson).catch(e => console.warn('Could not register person to backend:', e));
      }

      setCurrentUser(updatedUser);
      setLoading(false);
      return updatedUser;
    } catch (err) {
      setAuthError(err.message);
      setLoading(false);
      throw err;
    }
  };

  // Quick Demo Login helper
  const loginAsDemo = (role = 'user') => {
    if (role === 'admin') {
      setCurrentUser({
        email: 'admin@skillmesh.io',
        name: 'SkillMesh Admin',
        role: 'admin',
        title: 'Platform Administrator',
        bio: 'Managing skill graphs, cross-functional teams, and talent matching.',
        availability: 'Available',
        skills: [
          { name: 'Product Strategy', level: 'Expert', category: 'Business', experienceYears: 6 },
          { name: 'Data Analysis', level: 'Advanced', category: 'AI/Data', experienceYears: 5 }
        ],
        projects: []
      });
    } else {
      setCurrentUser({
        email: 'builder@skillmesh.io',
        name: 'Elena Vancea',
        role: 'user',
        title: 'Full-Stack Software Engineer',
        bio: 'Passionate about building modern applications, web platforms, and collaborative projects.',
        availability: 'Available',
        skills: [
          { name: 'React', level: 'Advanced', category: 'Frontend', experienceYears: 3 },
          { name: 'Python', level: 'Intermediate', category: 'Backend', experienceYears: 2 },
          { name: 'FastAPI', level: 'Intermediate', category: 'Backend', experienceYears: 2 },
          { name: 'UI/UX Design', level: 'Intermediate', category: 'Design', experienceYears: 1 }
        ],
        projects: []
      });
    }
  };

  // Toggle user / admin role
  const toggleRole = () => {
    setCurrentUser(prev => ({
      ...prev,
      role: prev.role === 'admin' ? 'user' : 'admin'
    }));
  };

  // Logout current user
  const logout = () => {
    localStorage.removeItem('skillmesh_user');
    setCurrentUser(null);
  };

  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);

  // Auto-check for first-time onboarding
  useEffect(() => {
    if (currentUser && currentUser.onboardingCompleted !== true && (!currentUser.skills || currentUser.skills.length === 0)) {
      // Don't auto-open if dismissed in session storage
      const dismissed = sessionStorage.getItem('skillmesh_onboarding_dismissed');
      if (!dismissed) {
        setIsOnboardingOpen(true);
      }
    }
  }, [currentUser]);

  const openOnboarding = () => setIsOnboardingOpen(true);
  const closeOnboarding = () => {
    setIsOnboardingOpen(false);
    sessionStorage.setItem('skillmesh_onboarding_dismissed', 'true');
  };

  // Update user profile and skills
  const updateUserProfile = (updatedData) => {
    setCurrentUser(prev => ({
      ...prev,
      ...updatedData
    }));
  };

  const ADMIN_EMAILS = ['adrian@leanistic.com', 'admin@skillmesh.io'];
  const isAdmin = currentUser?.role === 'admin' || ADMIN_EMAILS.includes(currentUser?.email?.toLowerCase());

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAdmin,
        loading,
        authError,
        sendMagicLink,
        verifyMagicToken,
        logout,
        loginAsDemo,
        toggleRole,
        updateUserProfile,
        isOnboardingOpen,
        openOnboarding,
        closeOnboarding
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
